import { showUserAlert } from "../utils/userErrorMessage";
import React, { useEffect, useMemo, useState } from "react";
import Header from "../Component/Header";
import { supabase } from "../supabase/client";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import usePersistedUI from "../hooks/usePersistedUI";

const firstDayOfMonth = () => {
  const d = new Date();
  d.setDate(1);
  return d.toISOString().slice(0, 10);
};

const today = () => new Date().toISOString().slice(0, 10);

const parseMoney = (value) => {
  const num = Number(String(value || "").replace(/[^\d.-]/g, ""));
  return Number.isFinite(num) ? num : 0;
};

const formatMoney = (value) => {
  const num = parseMoney(value);
  if (!num && !String(value || "").trim()) return "";
  return num ? num.toLocaleString() : value;
};

const isImageUrl = (value = "") =>
  /^https?:\/\//i.test(value) && /\.(png|jpe?g|gif|webp|bmp)(\?|#|$)/i.test(value);

export default function Receipts() {
  const company = localStorage.getItem("email") || "";
  const ui = usePersistedUI(
    "receipts",
    {
      dateRange: { from: firstDayOfMonth(), to: today() },
      filters: { 전화: "", 내용: "" },
    },
    { urlSync: true }
  );
  const { state } = ui;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const totalMoney = useMemo(
    () => rows.reduce((sum, row) => sum + parseMoney(row.money), 0),
    [rows]
  );

  const fetchData = async () => {
    if (!company) {
      showUserAlert("회사 식별 정보가 없습니다. 다시 로그인해 주세요.");
      return;
    }

    setLoading(true);
    try {
      const { from, to } = state.dateRange || {};
      const phone = state.filters?.전화?.trim() || "";
      const detail = state.filters?.내용?.trim() || "";

      let query = supabase
        .from("receipts")
        .select("id, created_at, driver_email, company, date, detail, money, picture, phone")
        .eq("company", company)
        .order("date", { ascending: false, nullsFirst: false })
        .order("created_at", { ascending: false })
        .range(0, 1999);

      if (from) query = query.gte("date", from);
      if (to) query = query.lte("date", to);
      if (phone) query = query.ilike("phone", `%${phone}%`);
      if (detail) query = query.ilike("detail", `%${detail}%`);

      const { data, error } = await query;
      if (error) throw error;
      setRows(data || []);
    } catch (e) {
      showUserAlert("영수증 처리 내역을 불러오지 못했습니다: " + (e?.message || "알 수 없는 오류"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-1">
      <Header />
      <div className="px-4 pt-1 pb-4 space-y-2">
        <h2 className="text-lg font-semibold">영수증 처리 내역</h2>

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm">영수증일:</span>
          <Input type="date" {...ui.bind("dateRange.from")} />
          <span>~</span>
          <Input type="date" {...ui.bind("dateRange.to")} />
          <Input className="w-36" placeholder="전화번호" {...ui.bind("filters.전화")} />
          <Input className="w-52" placeholder="내용" {...ui.bind("filters.내용")} />
          <Button onClick={fetchData} disabled={loading}>
            {loading ? "조회중..." : "검색"}
          </Button>
        </div>

        <Card className="border-0 shadow-none">
          <CardContent className="bg-white p-0">
            <div className="mb-2 flex items-center justify-between text-sm">
              <div className="text-gray-600">총 {rows.length}건</div>
              <div className="font-semibold">합계 {totalMoney.toLocaleString()}원</div>
            </div>

            <div className="relative max-h-[72vh] overflow-auto">
              <table className="table-auto w-full border text-sm">
                <thead className="sticky top-0 z-10 bg-gray-100">
                  <tr>
                    <th className="border px-2 py-1 text-center w-12">순번</th>
                    <th className="border px-2 py-1 text-center w-28">영수증일</th>
                    <th className="border px-2 py-1 text-center w-36">등록일</th>
                    <th className="border px-2 py-1 text-center w-32">전화번호</th>
                    <th className="border px-2 py-1 text-center">내용</th>
                    <th className="border px-2 py-1 text-center w-32">금액</th>
                    <th className="border px-2 py-1 text-center w-28">사진</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => (
                    <tr key={row.id}>
                      <td className="border px-2 py-1 text-center">{index + 1}</td>
                      <td className="border px-2 py-1 text-center">{row.date || ""}</td>
                      <td className="border px-2 py-1 text-center">
                        {(row.created_at || "").replace("T", " ").slice(0, 16)}
                      </td>
                      <td className="border px-2 py-1 text-center">{row.phone || ""}</td>
                      <td className="border px-2 py-1 text-left whitespace-pre-wrap">{row.detail || ""}</td>
                      <td className="border px-2 py-1 text-right">{formatMoney(row.money)}</td>
                      <td className="border px-2 py-1 text-center">
                        {row.picture ? (
                          <a
                            href={row.picture}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center justify-center text-blue-600 underline"
                          >
                            {isImageUrl(row.picture) ? (
                              <img src={row.picture} alt="영수증" className="h-10 max-w-20 object-contain" />
                            ) : (
                              "보기"
                            )}
                          </a>
                        ) : (
                          ""
                        )}
                      </td>
                    </tr>
                  ))}
                  {!loading && rows.length === 0 && (
                    <tr>
                      <td className="border px-2 py-6 text-center text-gray-500" colSpan={7}>
                        조회된 영수증 처리 내역이 없습니다.
                      </td>
                    </tr>
                  )}
                  {loading && (
                    <tr>
                      <td className="border px-2 py-6 text-center text-gray-500" colSpan={7}>
                        불러오는 중...
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
